'use client';
import { useState } from 'react';
import { Drawer, Group, Text, Button, Popover } from '@mantine/core';
import { IconCheck, IconRefresh } from '@tabler/icons-react';

// Bottom-sheet met alle zakken als kaartjes: nummer + mini-voortgangsbalk,
// vinkje-badge als de zak klaar is, blauwe ring voor de actieve zak.
// Vervangt de sticky quick-jump binnen de sort mode. Onderin (met
// bevestiging) de enige destructieve actie: alle voortgang resetten.
export function BagPickerSheet({ opened, onClose, bags, bagCompletion, activeIndex, onSelect, shortLabel, totalChecked = 0, onResetAll }) {
  const doneCount = bags.filter(b => (bagCompletion[b.bagNumber] || 0) >= 100).length;
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      title={
        <Group gap={8} align="baseline">
          <Text fw={700}>Jump to bag</Text>
          <Text size="sm" c="dimmed">{doneCount} of {bags.length} done</Text>
        </Group>
      }
      radius="lg"
      size="auto"
      zIndex={400}
      // Sheet omarmt zijn inhoud vanaf de onderkant; bij veel zakken groeit hij
      // tot max ~3/4 scherm en scrollt de body intern
      styles={{
        inner: { alignItems: 'flex-end' },
        content: { height: 'auto', minHeight: 0, maxHeight: '78dvh', display: 'flex', flexDirection: 'column' },
        body: { overflowY: 'auto', minHeight: 0 },
      }}
    >
      <div className="sort-bagpicker-grid">
        {bags.map((bag, i) => {
          const pct = Math.min(100, bagCompletion[bag.bagNumber] || 0);
          const done = pct >= 100;
          const active = i === activeIndex;
          return (
            <button
              key={bag.bagNumber}
              type="button"
              className="sort-bagchip"
              data-done={done || undefined}
              data-active={active || undefined}
              aria-current={active ? 'true' : undefined}
              aria-label={`${shortLabel(bag)}: ${Math.floor(pct)}% done`}
              onClick={() => { onSelect(i); onClose(); }}
            >
              {done && (
                <span className="sort-bagchip-badge" aria-hidden="true">
                  <IconCheck size={11} stroke={3.5} />
                </span>
              )}
              <span className="sort-bagchip-num">{shortLabel(bag)}</span>
              <span className="sort-bagchip-track" aria-hidden="true">
                <i style={{ width: `${pct}%` }} />
              </span>
            </button>
          );
        })}
      </div>

      {onResetAll && totalChecked > 0 && (
        <Group justify="flex-end" pt={4} pb={6}>
          <Popover width={240} position="top-end" withArrow shadow="md" zIndex={410} opened={confirmResetOpen} onChange={setConfirmResetOpen}>
            <Popover.Target>
              <Button size="compact-xs" variant="subtle" color="red" leftSection={<IconRefresh size={13} />} onClick={() => setConfirmResetOpen(o => !o)}>
                Reset all progress
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Text size="sm" fw={600} mb={4}>Reset all progress?</Text>
              <Text size="xs" c="dimmed" mb="xs">
                This resets all {totalChecked} checked pieces of this set, including progress synced to your account.
              </Text>
              <Group gap="xs" justify="flex-end">
                <Button size="compact-xs" variant="default" onClick={() => setConfirmResetOpen(false)}>Cancel</Button>
                <Button size="compact-xs" color="red" onClick={() => { setConfirmResetOpen(false); onResetAll(); onClose(); }}>Reset</Button>
              </Group>
            </Popover.Dropdown>
          </Popover>
        </Group>
      )}
    </Drawer>
  );
}
